import sdkAccessControlService, {
  ApiKeyAccessInput,
  InputPageable,
  InputUserInfoRecord,
  PayDaApplicationDto,
  PosApplication,
  ProductRoleEnum,
  TaskStates,
  TaskType,
} from '@/app/graphql/access-control/access-control';
import {
  mapApiKey,
  mapEmployeeContract,
  mapEmployeeSecure,
  mapPosApplicationTask,
  mapPosApplicationTasks,
  mapSellerInstallment,
} from './helps';

export async function getEmployeeSecure() {
  const responseEmployeeSecure = await sdkAccessControlService.employeeSecure();
  return mapEmployeeSecure(responseEmployeeSecure.employeeSecure);
}

export async function getEmployee(userReferenceId: number) {
  const responseEmployeeSecure = await sdkAccessControlService.employee({ userReferenceId });
  return mapEmployeeSecure(responseEmployeeSecure.employee);
}

export async function getEmployeesContract(orgReferenceId: number) {
  const response = await sdkAccessControlService.employeesContract({ orgReferenceId });
  return mapEmployeeContract(response.employeesContract);
}

export async function getKeyAvailability(orgReferenceId: number) {
  const response = await sdkAccessControlService.keyAvailability({ orgReferenceId });
  return response.keyAvailability;
}

export async function createBusinessUserContractWithRoleSellerTask(
  userInfo: InputUserInfoRecord,
  orgReferenceId: number
) {
  const response = await sdkAccessControlService.createBusinessUserContractWithRoleSellerTask({
    userInfo,
    orgReferenceId,
  });
  return response.createBusinessUserContractWithRoleSellerTask;
}

export async function getSellers(
  taskState: TaskStates,
  pageableDecorator: InputPageable,
  individualInn: string | null,
  orgReferenceId: number,
  taskType: TaskType[]
) {
  const response = await sdkAccessControlService.getSellers({
    taskState,
    pageableDecorator,
    individualInn,
    orgReferenceId,
    taskType,
  });
  return mapSellerInstallment(response.commonTasksPageableFilter);
}

export async function savePosApplicationTask(businessUserContractId: string, posApplication: PosApplication) {
  const response = await sdkAccessControlService.savePosApplicationTask({ businessUserContractId, posApplication });
  return response.POSApplicationTask;
}

export async function userPOSApplicationTasksPageable(
  businessUserContractId: string,
  taskTypes: TaskType[],
  taskStates: TaskStates[],
  pageableDecorator: InputPageable
) {
  const response = await sdkAccessControlService.userPOSApplicationTasksPageable({
    businessUserContractId,
    taskTypes,
    taskStates,
    pageableDecorator,
  });
  return mapPosApplicationTasks(response.userPOSApplicationTasksPageable);
}

export async function getUserPOSApplicationTask(taskId: string, businessUserContractId: string) {
  const response = await sdkAccessControlService.getUserPOSApplicationTask({ taskId, businessUserContractId });
  return mapPosApplicationTask(response.userPOSApplicationTask);
}

export async function getApiKey(orgReferenceId: number) {
  const response = await sdkAccessControlService.apiKey({ orgReferenceId });
  return mapApiKey(response.apiKey);
}

export async function createApiKey(orgReferenceId: number, password: string, apiKeyAccessList: ApiKeyAccessInput[]) {
  const response = await sdkAccessControlService.createApiKey({ orgReferenceId, password, apiKeyAccessList });
  return response.createApiKey;
}

export async function savePayDaApplicationTask(businessUserContractId: string, payDaApplication: PayDaApplicationDto) {
  const response = await sdkAccessControlService.savePayDaApplicationTask({ businessUserContractId, payDaApplication });
  return response.payDaApplicationTask;
}

export async function switchProductRolePolicy(
  businessUserContractId: string,
  productRole: ProductRoleEnum,
  switchAccess: boolean
) {
  const response = await sdkAccessControlService.switchProductRolePolicy({
    productRole,
    switchAccess,
    businessUserContractId,
  });
  return response.switchProductRolePolicy;
}

export async function createCashierByLinkTaskPageable(
  contractId: string,
  taskStates: TaskStates[],
  pageableDecorator: InputPageable
) {
  const response = await sdkAccessControlService.createCashierByLinkTaskPageable({
    taskStates,
    pageableDecorator,
    contractId,
  });
  return response.createCashierByLinkTaskPageable;
}

export async function countCashierByLinkTaskPageable(
  contractId: string,
  taskStates: TaskStates[],
  pageableDecorator: InputPageable
) {
  const response = await sdkAccessControlService.countCashierByLinkTaskPageable({
    taskStates,
    pageableDecorator,
    contractId,
  });
  return response.createCashierByLinkTaskPageable.totalElements;
}

export async function verdictForTask(taskId: string, verdict: boolean, reason: string | null = null) {
  const response = await sdkAccessControlService.verdictForTask({ taskId, verdict, reason });
  return response.verdictForTask;
}

/** JSON-схемы markdown-шаблонов задачи на подписи */
export async function getMarkdownTemplateSchemas(taskId: string) {
  const response = await sdkAccessControlService.markdownTemplateSchemas({ taskId });
  return response.markdownTemplateSchemas;
}

/** сохраняет заполненные markdown-шаблоны задачи; для PayControl бэк сразу отправляет задачу на подпись */
export async function createMarkdown(taskId: string, markdownTemplates: string[]) {
  const response = await sdkAccessControlService.createMarkdown({ taskId, markdownTemplates });
  return response.createMarkdown;
}

export async function individualPerson(inn: string) {
  const response = await sdkAccessControlService.individualPerson({ inn });
  return response.individualPerson;
}
